import Modal from "patient-ping-remedy/packages/modal";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {css} from "@emotion/css";
import {Fragment, lazy, useContext, useEffect, useState} from "react";
import RadioButton from "patient-ping-remedy/packages/radio-button";
import {
  PerformedByLabel,
  RadioButtonWrapper
} from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentModal.styles";
import {StandardSemiBoldTypography, StandardTypography} from "patient-ping-remedy/packages/typography";
import DropdownList from "patient-ping-remedy/packages/dropdown-list";
import {Value} from "patient-ping-remedy/packages/dropdown-list/lib/DropdownList";
import RemoteComponent from "@common/RemoteComponent";
import Loading from "@common/Loading";
import {useActiveRoster, useRosterStore} from "@/store/roster_store";
import {Contact, Unit} from "@/api/dto/thoth";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
  CareJourneyDetailsResponse,
  CareJourneyStep, CareJourneyType,
  ScheduleAppointmentAiRequest,
  ScheduleAppointmentHumanRequest,
  ScheduleAppointmentRequest,
  TaskStepName
} from "@/api/dto/care-journey";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {AxiosError} from "axios";
import Helpers from "@/helpers/helpers";
import {GENERICERROR} from "@manual_encounters/constants/BannerMessages";
import {useAlertStore} from "@/store/alert_store";
import {Address, ClientProfile} from "@/api/dto/client-profile";
import SurveyJSForm from "@common/survey_js/SurveyJSForm";
import {Model, SurveyModel} from "survey-core";
import {useSurveyJSForm} from "@/hooks/useSurveyJSForm";
import {
  appointmentTypeOptions
} from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentsStep.constants";
import Banner, {BannerTypes} from "patient-ping-remedy/packages/banner";
import {useAuthStore} from "@/store/auth_store";
import {convertLocalToTimezone, convertTimezoneToLocal} from "@/helpers/timezone_helpers";
import {useMixpanel} from "@/hooks/useMixpanel";
import {
  AppointmentSchedulingMode,
  AppointmentType,
  MixpanelAppointmentScheduledEvent,
  MixpanelEventType
} from "@/api/dto/mixpanel";

const NetworkRegistryPicker = lazy(() => import("thoth/CareJourneyNetworkBrowser"));

type Props = {
  open: boolean;
  close: () => void;
  careJourneyStep: CareJourneyStep;
  careJourneyDetails: CareJourneyDetailsResponse;
  patient: ClientProfile;
}

enum Step {
  PERFORMED_BY,
  APPOINTMENT_TYPE,
  CHOOSE_LOCATION,
  SCHEDULE_APPOINTMENT_FORM
}

enum PerformedBy {
  BAMBOO_AI = "BAMBOO_AI",
  CARE_NAVIGATOR = "CARE_NAVIGATOR",
}

const modalHeader = {
  [Step.PERFORMED_BY]: "Scheduling Performed By",
  [Step.APPOINTMENT_TYPE]: "Select Appointment Type",
  [Step.CHOOSE_LOCATION]: "Choose Location",
  [Step.SCHEDULE_APPOINTMENT_FORM]: "Scheduled Appointment Details",
};

type NormalizedAddress = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

const stripPhone = (phone?: string | null): string => (phone ?? '').replace(/\D/g, '');

const fromCohortAddress = (a?: Address | null): NormalizedAddress => ({
  line1: a?.address1, line2: a?.address2, city: a?.city, state: a?.state, zip: a?.zip,
});

const fromContactAddress = (c?: Contact | null): NormalizedAddress => ({
  line1: c?.address_line1, line2: c?.address_line2, city: c?.city, state: c?.state, zip: c?.postal_code,
});

const hasAddress = (a: NormalizedAddress) => !!(a.line1 || a.city || a.state || a.zip);

const formatAddressLine = (a: NormalizedAddress): string => {
  const line = [a.line1, a.line2].filter(Boolean).join(' ');
  const cityStateZip = [a.city, a.state].filter(Boolean).join(', ') + (a.zip ? ` ${a.zip}` : '');
  return [line, cityStateZip].filter(Boolean).join(', ');
};

const toRequestAddress = (a: NormalizedAddress) => ({
  street1: [a.line1, a.line2].filter(Boolean).join(' '),
  city: a.city ?? '',
  state: a.state ?? '',
  zip: a.zip ?? '',
});

const resolveUnitAddressContact = (unit: Unit): Contact | undefined =>
  unit.contact?.find(c => hasAddress(fromContactAddress(c)))
    ?? unit.primary_contact
    ?? unit.bridge_contact
    ?? unit.facility?.contact?.find(c => hasAddress(fromContactAddress(c)))
    ?? unit.facility?.primary_contact;

const resolveUnitPhone = (unit: Unit): string =>
  stripPhone(
    unit.contact?.find(c => c.phone)?.phone
      ?? unit.facility?.contact?.find(c => c.phone)?.phone
  );

const getModalSize = (step: Step): string => {
  switch (step) {
  case Step.PERFORMED_BY:
    return "70rem";
  case Step.APPOINTMENT_TYPE:
    return "70rem";
  case Step.CHOOSE_LOCATION:
    return "120rem";
  case Step.SCHEDULE_APPOINTMENT_FORM:
    return "100rem";
  }
};

const ScheduleAppointmentModal = (props : Props) => {
  const rosterStore = useRosterStore();
  const currentRoster = useActiveRoster();
  const {carecoApi} = useContext(CarecoApiContext);
  const authStore = useAuthStore();
  const {currentUser} = authStore;
  const {addAlert} = useAlertStore();
  const queryClient = useQueryClient();
  const {scrollToError} = useSurveyJSForm();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();

  const [step, setStep] = useState(Step.APPOINTMENT_TYPE);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<null | Value>(null);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [performedBy, setPerformedBy] = useState<PerformedBy | null>(null);
  const [surveyModel, setSurveyModel] = useState<Model | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  const mutate = useMutation({
    mutationFn: async (request: ScheduleAppointmentRequest) =>
      performedBy === PerformedBy.BAMBOO_AI ?
            carecoApi!.createScheduledAiAppointment(request, currentRoster!.id)
        : carecoApi!.createScheduledCareNavigatorAppointment(request, currentRoster!.id),
    onSuccess: () => {
      if(mixpanelEventProperties && currentRoster && selectedAppointmentType && performedBy) {
        const event: MixpanelAppointmentScheduledEvent = {
          eventName: MixpanelEventType.BIH_APPOINTMENT_SCHEDULED,
          eventType: props.careJourneyDetails.eventType,
          careJourneyName: props.careJourneyDetails.journeyType,
          appointmentType: selectedAppointmentType.value as AppointmentType,
          appointmentSchedulingMode: performedBy === PerformedBy.BAMBOO_AI
            ? AppointmentSchedulingMode.AI
            : AppointmentSchedulingMode.HITL,
          ...mixpanelEventProperties,
          ...getCurrentGroupDetails(currentRoster)
        };
        carecoApi?.postMixpanelEvent(event);
      }
      addAlert({
        content: 'Appointment schedule added successfully.',
        type: ToastType.SUCCESS
      });
      void queryClient.refetchQueries({queryKey: ['careJourneyDetails', props.careJourneyDetails.careJourneyInstanceId]});
      props.close();
    },
    onError: (error : AxiosError) => {
      Helpers.alertOnError(`${GENERICERROR} ${Helpers.traceId(error)}`, error);
    }
  });

  useEffect(() => {
    if(!props.open) {
      setStep(Step.APPOINTMENT_TYPE);
      setPerformedBy(null);
      setSelectedAppointmentType(null);
      setSelectedUnit(null);
      setSurveyModel(null);
    }
  }, [props.open]);

  useEffect(() => {
    setPerformedBy(null);
    setSelectedUnit(null);
    setSurveyModel(null);
  }, [selectedAppointmentType]);

  useEffect(() => {
    if(performedBy === PerformedBy.BAMBOO_AI || !surveyModel) {
      surveyModel?.clear();
      return;
    }

    const baseData : Record<string, any> = {
      pcp_appointment: true,
    };

    if(selectedAppointmentType?.value === 'pcp') {
      if (props.patient.primaryCareProvider) {
        baseData['scheduled_with'] =
          `${props.patient.primaryCareProvider.firstName ?? ''} ${props.patient.primaryCareProvider.lastName ?? ''}`.trim();
        baseData['phone_number_to_location'] = stripPhone(props.patient.primaryCareProvider.telephone);
      }

      if (props.patient.primaryCareProviderCohort) {
        baseData['scheduled_with'] = baseData['scheduled_with']
          ? `${props.patient.primaryCareProviderCohort.name} - ${baseData['scheduled_with']}`
          : (props.patient.primaryCareProviderCohort.name ?? '');

        baseData['location'] = formatAddressLine(fromCohortAddress(props.patient.primaryCareProviderCohort.address));
      }
    }
    else if (selectedUnit) {
      const names = [selectedUnit.facility?.name, selectedUnit.name]
        .filter((n): n is string => !!n)
        .filter((name, idx, arr) => arr.indexOf(name) === idx);

      baseData['scheduled_with'] = names.join(' - ');
      baseData['phone_number_to_location'] = resolveUnitPhone(selectedUnit);
      baseData['location'] = formatAddressLine(fromContactAddress(resolveUnitAddressContact(selectedUnit)));
    }

    surveyModel.mergeData(baseData);
  }, [performedBy, surveyModel, selectedAppointmentType, selectedUnit, props.patient]);

  const handleAddAppointment = () => {
    if(performedBy === PerformedBy.BAMBOO_AI) {
      const request = buildAppointmentRequest();
      mutate.mutate(request);
      return;
    }

    if(step === Step.PERFORMED_BY) {
      setStep(Step.SCHEDULE_APPOINTMENT_FORM);

      setFormLoading(true);
      carecoApi?.getCareJourneyStepForm(
        TaskStepName.SCHEDULED_APPOINTMENT,
          currentRoster!.id,
          CareJourneyType.BH_SCHEDULE_APPOINTMENT,
          props.careJourneyDetails.careJourneyInstanceId
      )
        .then(response => {
          setSurveyModel(new SurveyModel(response));
        }).catch(error =>
          Helpers.alertOnError(`Failed to load form. ${GENERICERROR} ${Helpers.traceId(error)}`, error)
        ).finally(() => setFormLoading(false));

      return;
    }

    const data = surveyModel?.data || {};
    const isValid = surveyModel?.validate();

    const local = convertTimezoneToLocal(data.appointment_datetime, Intl.DateTimeFormat().resolvedOptions().timeZone);
    data.appointment_datetime = convertLocalToTimezone(local, data.appointment_timezone);

    if (isValid) {
      const request : ScheduleAppointmentHumanRequest = {
        customerId: props.careJourneyDetails.organizationId?.toString() ?? currentRoster!.organization!.id,
        journeyType: CareJourneyType.BH_SCHEDULE_APPOINTMENT,
        parentJourneyId: props.careJourneyDetails.careJourneyInstanceId,
        providerType: selectedAppointmentType!.value,
        appointmentType: 'pcp',
        confirmationChannel: 'channel_selection',
        appointmentDateTime: data.appointment_datetime,
        appointmentSetting: data.in_person_or_virtual,
        scheduledWith: data.scheduled_with,
        location: data.location,
        form: {
          ...data,
          user_id: currentUser!.id,
        },
      };
      mutate.mutate(request);
    } else {
      scrollToError();
    }
  };
  const buildPcpProviderOrganization = () : ScheduleAppointmentAiRequest['providerOrganization'] => ({
    name: props.patient.primaryCareProviderCohort?.name ?? '',
    phoneNumber: stripPhone(props.patient.primaryCareProviderCohort?.telephone),
    ...(props.patient.primaryCareProviderCohort?.address
      ? { address: toRequestAddress(fromCohortAddress(props.patient.primaryCareProviderCohort.address)) }
      : {}),
  });

  const buildNetworkRegistryProviderOrganization = () : ScheduleAppointmentAiRequest['providerOrganization'] => {
    const unitContact = selectedUnit ? resolveUnitAddressContact(selectedUnit) : undefined;
    const normalized = fromContactAddress(unitContact);

    return {
      name: selectedUnit?.facility?.name ?? selectedUnit?.name ?? '',
      phoneNumber: selectedUnit ? resolveUnitPhone(selectedUnit) : '',
      ...(hasAddress(normalized) ? { address: toRequestAddress(normalized) } : {}),
    };
  };

  const buildAppointmentRequest = () : ScheduleAppointmentAiRequest => {
    const isPcp = selectedAppointmentType?.value === 'pcp';
    const hasPcpProvider = isPcp && !!props.patient.primaryCareProvider;
    const pcp = props.patient.primaryCareProvider;

    return {
      customerId: props.careJourneyDetails.organizationId?.toString() ?? currentRoster!.organization!.id,
      journeyType: CareJourneyType.BH_SCHEDULE_APPOINTMENT,
      parentJourneyId: props.careJourneyDetails.careJourneyInstanceId,
      immediate: process.env.REACT_APP_OE_APPOINTMENT_IMMEDIATE_MODE === 'true',
      providerOrganization: hasPcpProvider
        ? buildPcpProviderOrganization()
        : buildNetworkRegistryProviderOrganization(),
      ...(hasPcpProvider && pcp ? {
        primaryCareProvider: {
          firstName: pcp.firstName,
          lastName: pcp.lastName,
          phoneNumber: stripPhone(pcp.telephone),
          npi: pcp.npi,
        },
      } : {}),
      appointmentType: isPcp ? 'pcp' : 'bh',
      providerType: selectedAppointmentType!.value,
    };
  };

  const resolveNextStep = () => {
    switch (step) {
    case Step.APPOINTMENT_TYPE:
      if(selectedAppointmentType) {
        if(selectedAppointmentType.value === "pcp" && props.patient.primaryCareProvider) {
          setStep(Step.PERFORMED_BY);
        } else {
          setStep(Step.CHOOSE_LOCATION);
        }
      }
      break;
    case Step.CHOOSE_LOCATION:
      setStep(Step.PERFORMED_BY);
      break;
    default:
      break;
    }
  };

  const resolvePreviousStep = () => {
    switch (step) {
    case Step.PERFORMED_BY:
      if(selectedAppointmentType){
        if(selectedAppointmentType.value === "pcp" && props.patient.primaryCareProvider) {
          setStep(Step.APPOINTMENT_TYPE);
        } else {
          setStep(Step.CHOOSE_LOCATION);
        }
      } else {
        setStep(Step.APPOINTMENT_TYPE);
      }
      break;
    case Step.CHOOSE_LOCATION:
      setStep(Step.APPOINTMENT_TYPE);
      break;
    case Step.SCHEDULE_APPOINTMENT_FORM:
      setSurveyModel(null);
      setStep(Step.PERFORMED_BY);
      break;
    default:
      break;
    }
  };

  const resolveButtons = () => {
    const buttons = [
      <Button
        key="cancel"
        id={"schedule-appointment-cancel-button"}
        styleType={StyleType.TERTIARY}
        onClick={props.close}
      >
        Cancel
      </Button>
    ];

    if (step !== Step.APPOINTMENT_TYPE) {
      buttons.push(
        <Button
          key="back"
          id={"schedule-appointment-back-button"}
          styleType={StyleType.SECONDARY}
          onClick={resolvePreviousStep}
          disabled={mutate.isPending}
        >
          Back
        </Button>
      );
    }

    if(step === Step.APPOINTMENT_TYPE || step === Step.CHOOSE_LOCATION) {
      const disabled = (step === Step.APPOINTMENT_TYPE && !selectedAppointmentType)
        || (step === Step.CHOOSE_LOCATION && !selectedUnit);
      buttons.push(
        <Button
          key="next"
          id={"schedule-appointment-next-button"}
          onClick={() => resolveNextStep()}
          disabled={disabled || mutate.isPending}
        >
          Next
        </Button>
      );
    }

    if(step === Step.PERFORMED_BY || step === Step.SCHEDULE_APPOINTMENT_FORM) {
      buttons.push(
        <Button
          key="add-appointment"
          id={"schedule-appointment-add-button"}
          disabled={!performedBy || mutate.isPending}
          onClick={() => handleAddAppointment()}
        >
          Add Appointment
        </Button>
      );
    }

    return buttons;
  };

  const renderPerformedByOption = (
    id: string,
    value: PerformedBy,
    title: string,
    description: string,
  ) => (
    <RadioButtonWrapper
      onClick={() => { if (!mutate.isPending) setPerformedBy(value); }}
    >
      <RadioButton
        id={id}
        name={'performed_by'}
        label={""}
        value={value}
        checked={performedBy === value}
        onChange={() => setPerformedBy(value)}
        disabled={mutate.isPending}
      />
      <PerformedByLabel>
        <StandardSemiBoldTypography>{title}</StandardSemiBoldTypography>
        <StandardTypography>{description}</StandardTypography>
      </PerformedByLabel>
    </RadioButtonWrapper>
  );

  const getPerformedByContent = () => (
    <Fragment>
      {renderPerformedByOption(
        "performed_by_bamboo_ai",
        PerformedBy.BAMBOO_AI,
        "Bamboo AI Agent",
        "Appointment is scheduled automatically by the AI agent.",
      )}
      {renderPerformedByOption(
        "performed_by_care_navigator",
        PerformedBy.CARE_NAVIGATOR,
        "Care Navigator",
        "Appointment is scheduled manually by the user.",
      )}
    </Fragment>
  );

  const getAppointmentTypeContent = () => {
    return <DropdownList
      id={'scheduling-appointment-type-dropdown'}
      handleChange={(value: Value) => setSelectedAppointmentType(value)}
      label={"Type"}
      value={selectedAppointmentType}
      placeholder={"Select appointment type"}
      items={appointmentTypeOptions}
      required
      isWithinModal
    />;
  };

  const getChooseLocationContent = () => {
    if (!currentRoster) {
      return <Loading />;
    }

    return (
      <RemoteComponent
        remote="thoth"
        component={NetworkRegistryPicker}
        authStore={authStore}
        rosterStore={rosterStore}
        facilityId={currentRoster.uuid}
        unitId={selectedUnit?.id}
        onSelect={(unit: Unit | null) => setSelectedUnit(unit)}
      />
    );
  };

  const getScheduleAppointmentFormContent = () => (
    surveyModel
      ? <SurveyJSForm surveyModel={surveyModel} />
      : <Banner type={BannerTypes.WARNING}>Failed to load Scheduled Appointment form.</Banner>
  );

  return <Modal
    id={"schedule-appointment-modal"}
    className={css({width: getModalSize(step)})}
    isOpen={props.open}
    headerText={modalHeader[step]}
    buttons={resolveButtons()}
  >
    {formLoading ? <Loading /> : <>
      {step === Step.PERFORMED_BY && getPerformedByContent()}
      {step === Step.APPOINTMENT_TYPE && getAppointmentTypeContent()}
      {step === Step.CHOOSE_LOCATION && getChooseLocationContent()}
      {step === Step.SCHEDULE_APPOINTMENT_FORM && getScheduleAppointmentFormContent()}
    </> }
  </Modal>;
};

export default ScheduleAppointmentModal;
